import styled from "styled-components";
import * as Dialog from "@radix-ui/react-dialog";
import { Label } from "@radix-ui/react-label";
import React, { useState } from "react";
import { ButtonRow, FormRow } from "../../components/dialog";

export const SubmitDollarVotePanelContents = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  row-gap: 1vh;
  width: 84vw;
  margin-inline: auto;
`;

const EntryWrapper = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 1vh;
  width: 90%;
`;

export const DollarEntryComponent = styled.div`
  display: flex;
  flex-direction: row;
`;

const RequestValueInput = styled.input`
  max-width: 9vh;
  font-size: 5vh;
  padding-left: 3vh;
`;

const VoucherInput = styled.input`
  box-sizing: border-box;
  max-width: 100%;
  font-size: 5vh;
  font-variant: all-small-caps;
  text-align: center;
`;

const CurrencyLabel = styled.div`
  position: relative;
  right: -1vh;
  width: 0;
  font-size: 3.5vh;
  color: darkslategrey;
  top: 0.25vh;
`;

const UseVoucherButton = styled.button`
  flex: 1;
  min-width: 0;
  min-height: 8vh;
  margin: 0;
  font-size: 3.5vh;
`;

const PaymentMethodRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1vh;
  width: 90%;
`;

const PaymentMethodButton = styled.button`
  flex: 1;
  min-width: 0;
  min-height: 10vh;
  max-height: 14vh;
  margin: 0;
  font-size: 3.5vh;
  padding: 0.5vh 0.25vh;
  white-space: nowrap;
`;

const DialogMessage = styled(Dialog.Description)`
  margin: 2vh;
  font-size: 3vh;
`;

const DialogButton = styled.button`
  width: fit-content;
  font-size: 3vh;
`;

const DialogActionRow = styled(ButtonRow)`
  flex-wrap: wrap;
  gap: 1vh;
`;

interface DollarVote {
  value: number;
  voucher?: string;
}

interface SubmitDollarVoteResult {
  paymentUrl?: string;
  success?: boolean;
}

const redirectToPayment = (url: string) => {
  window.location.href = url;
};

export const SubmitDollarVotePanel = ({
  onSubmit,
  redirectToPayment: redirectToPaymentProp = redirectToPayment,
}: {
  onSubmit: (
    vote: DollarVote,
  ) => Promise<SubmitDollarVoteResult | void> | SubmitDollarVoteResult | void;
  redirectToPayment?: (url: string) => void;
}) => {
  const [requestValue, setRequestValue] = useState("5");
  const [voucher, setVoucher] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
  const [supportPaymentMethod, setSupportPaymentMethod] = useState<
    "PayPal" | "Cash" | undefined
  >();

  const isReady = () => {
    if (!/^\d+$/.test(requestValue)) {
      return false;
    }

    const asInteger = Number.parseInt(requestValue);
    return Number.isSafeInteger(asInteger) && asInteger > 0 && asInteger < 1000;
  };

  const isVoucherReady = () => /^[a-zA-Z0-9]{6}$/.test(voucher);
  const currentDollarAmount = () =>
    isReady() ? Number.parseInt(requestValue) : undefined;

  const submitVote = async (voucherCode: string) => {
    setSubmitting(true);
    try {
      const result = await onSubmit({
        value: Number.parseInt(requestValue),
        voucher: voucherCode,
      });
      if (result?.paymentUrl) {
        redirectToPaymentProp(result.paymentUrl);
      }
      if (voucherCode && result?.success === true) {
        setVoucherDialogOpen(false);
        setVoucher("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const requestButtonsDisabled = submitting || !isReady();
  const supportButtonsDisabled = submitting;
  const supportPaymentMessage =
    supportPaymentMethod === "PayPal"
      ? "To pay with PayPal, buy a voucher at the support table, then enter the voucher code here."
      : "To pay with cash, buy a voucher at the support table, then enter the voucher code here.";

  return (
    <>
      <SubmitDollarVotePanelContents
        className={"SubmitDollarVotePanelContents"}
      >
        <EntryWrapper className={"EntryWrapper"}>
          <DollarEntryComponent className="DollarEntryComponent">
            <CurrencyLabel>$</CurrencyLabel>
            <RequestValueInput
              name={"requestValue"}
              aria-label={"Dollar amount"}
              value={requestValue}
              required={true}
              inputMode={"numeric"}
              minLength={1}
              min={1}
              max={999}
              type={"number"}
              onInput={(e) => {
                setRequestValue(e.currentTarget.value);
              }}
            />
          </DollarEntryComponent>
          <UseVoucherButton
            type={"button"}
            disabled={requestButtonsDisabled}
            onClick={() => setVoucherDialogOpen(true)}
          >
            Use voucher
          </UseVoucherButton>
        </EntryWrapper>

        <PaymentMethodRow>
          <PaymentMethodButton
            type={"button"}
            aria-label={
              currentDollarAmount()
                ? `Pay $${currentDollarAmount()} with Venmo`
                : "Pay with Venmo"
            }
            disabled={requestButtonsDisabled}
            onClick={() => submitVote("")}
          >
            Venmo
          </PaymentMethodButton>
          <PaymentMethodButton
            type={"button"}
            disabled={supportButtonsDisabled}
            onClick={() => setSupportPaymentMethod("PayPal")}
          >
            PayPal
          </PaymentMethodButton>
          <PaymentMethodButton
            type={"button"}
            disabled={supportButtonsDisabled}
            onClick={() => setSupportPaymentMethod("Cash")}
          >
            Cash
          </PaymentMethodButton>
        </PaymentMethodRow>
      </SubmitDollarVotePanelContents>

      <Dialog.Root
        modal={true}
        open={voucherDialogOpen}
        onOpenChange={(open) => {
          if (!submitting || open) {
            setVoucherDialogOpen(open);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="DialogOverlay">
            <Dialog.Content
              className="DialogContent"
              onOpenAutoFocus={(event) => event.preventDefault()}
            >
              <Dialog.Title>Use voucher</Dialog.Title>
              <DialogMessage>
                Apply ${Number.parseInt(requestValue) || 0} from a voucher.
                Enter the 6-character code.
              </DialogMessage>
              <FormRow>
                <Label className="formLabel" htmlFor={"voucher"}>
                  Voucher code
                </Label>
                <VoucherInput
                  id={"voucher"}
                  name={"voucher"}
                  value={voucher}
                  required={true}
                  minLength={6}
                  maxLength={6}
                  type={"text"}
                  pattern="[a-zA-Z0-9]*"
                  autoCapitalize={"characters"}
                  autoCorrect={"off"}
                  onInput={(e) => {
                    setVoucher(e.currentTarget.value);
                  }}
                />
              </FormRow>
              <DialogActionRow>
                <DialogButton
                  type={"button"}
                  disabled={submitting || !isVoucherReady()}
                  onClick={() => submitVote(voucher)}
                >
                  Apply voucher
                </DialogButton>
                <Dialog.Close asChild={true}>
                  <DialogButton type={"button"} disabled={submitting}>
                    Cancel
                  </DialogButton>
                </Dialog.Close>
              </DialogActionRow>
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root
        modal={true}
        open={supportPaymentMethod !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setSupportPaymentMethod(undefined);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="DialogOverlay">
            <Dialog.Content className="DialogContent">
              <Dialog.Title>{supportPaymentMethod}</Dialog.Title>
              <DialogMessage>{supportPaymentMessage}</DialogMessage>
              <DialogActionRow>
                <Dialog.Close asChild={true}>
                  <DialogButton type={"button"}>Close</DialogButton>
                </Dialog.Close>
              </DialogActionRow>
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};
